<template>
  <div class="practice-page">
    <!-- 本番用アップロード進捗表示（Recording と同じ） -->
    <div v-if="showProductionUploadProgress" class="production-upload-progress-overlay">
      <div class="production-upload-progress-modal">
        <div class="upload-message">
          <h3>📤 データ送信中...</h3>
          <p>このままお待ちください。</p>
        </div>
        <div class="progress-container">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: productionUploadProgress.percentage + '%' }"
            ></div>
          </div>
          <div class="progress-text">
            {{ productionUploadProgress.completed }} / {{ productionUploadProgress.total }}
          </div>
        </div>
        <div v-if="uploadStatus === 'completed'" class="completion-message">
          ✅ 送信完了！
        </div>
      </div>
    </div>

    <!-- 動画エリア（署名付きURLで表示・不正アクセス対策） -->
    <div class="video-area">
      <div class="video-wrapper">
        <div v-if="videoLoading" class="video-placeholder">動画を読み込み中...</div>
        <div v-else-if="videoLoadError === 'forbidden'" class="video-placeholder video-error">
          このDayの動画は視聴できません
        </div>
        <div v-else-if="videoLoadError === 'error'" class="video-placeholder video-error">
          動画の読み込みに失敗しました
        </div>
        <template v-else>
          <video
            ref="videoRef"
            class="video-player"
            :src="videoSrc"
            :controls="!isAssignmentRecording"
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            playsinline
            preload="metadata"
            @error="onVideoLoadError"
          />
        </template>
      </div>
    </div>

    <!-- ボタンエリア（横向き: 右15%縦並び / 縦向き: 動画下に①②横一列・戻るは左下） -->
    <aside class="button-area">
      <div class="action-buttons">
        <!-- 録音中は停止ボタン表示（練習 or 課題のどちらか） -->
        <template v-if="isRecording">
          <div class="recording-status-inline">
            <span class="recording-label">
              {{ currentRecordingType === 'practice' ? '🎤 練習録音中...' : `🎤 ${currentAssignmentLabel ? currentAssignmentLabel + 'の' : ''}課題録音中...` }}
            </span>
            <span class="recording-time">{{ formatTime(displayTime) }}</span>
            <p v-if="currentRecordingType === 'assignment'" class="assignment-notice">
              課題録音中は動画を再生できません。
            </p>
            <button
              type="button"
              class="btn btn-stop-recording"
              :disabled="!canStopRecording"
              @click="stopRecording"
            >
              ⏹️ 録音停止
            </button>
            <p v-if="currentRecordingType === 'assignment'" class="metacognitive-prompt">
              課題のあとに、今日いちばん頑張ったことを１つ教えてね！
            </p>
          </div>
        </template>
        <template v-else>
          <!-- disabled 中は @click が一切発火しないため guardHoliday のアラートも出ない。理由を常時表示する -->
          <p v-if="recordingStartBlockedReason" class="recording-block-notice" role="status">
            {{ recordingStartBlockedReason }}
          </p>
          <div class="action-with-status">
            <button
              type="button"
              class="btn btn-action"
              :disabled="isRecordingStartDisabled"
              @click="startPracticeWithVideo"
            >
              ① 練習録音
            </button>
            <p v-if="pendingPracticeSubmission" class="submitted-status">
              <span class="submitted-badge">送信済み</span>
              <span class="submitted-meta">{{ formatSubmittedAt(pendingPracticeSubmission.submittedAt) }}</span>
              <span class="submitted-meta">録音長さ {{ formatTime(pendingPracticeSubmission.durationSeconds) }}</span>
            </p>
          </div>
          <!-- 課題が複数ページのDay：ページごとにボタンを出す（それぞれ別の提出） -->
          <template v-if="hasMultipleAssignments">
            <div v-for="mark in assignmentMarks" :key="mark.partNo" class="action-with-status">
              <button
                type="button"
                class="btn btn-action"
                :disabled="isRecordingStartDisabled"
                @click="startAssignmentRecordingOnly(mark)"
              >
                ② 課題録音（{{ mark.label }}）
              </button>
              <p v-if="mark.partNo === lastAssignmentPartNo" class="metacognitive-prompt">
                課題のあとに、今日いちばん頑張ったことを１つ教えてね！
              </p>
              <p v-if="pendingAssignmentParts[mark.partNo]" class="submitted-status">
                <span class="submitted-badge">送信済み</span>
                <span class="submitted-meta">{{ formatSubmittedAt(pendingAssignmentParts[mark.partNo].submittedAt) }}</span>
                <span class="submitted-meta">録音長さ {{ formatTime(pendingAssignmentParts[mark.partNo].durationSeconds) }}</span>
              </p>
            </div>
          </template>
          <div v-else class="action-with-status">
            <button
              type="button"
              class="btn btn-action"
              :disabled="isRecordingStartDisabled"
              @click="startAssignmentRecordingOnly(assignmentMarks[0] || null)"
            >
              ② 課題録音
            </button>
            <p class="metacognitive-prompt">
              課題のあとに、今日いちばん頑張ったことを１つ教えてね！
            </p>
            <p v-if="pendingAssignmentSubmission" class="submitted-status">
              <span class="submitted-badge">送信済み</span>
              <span class="submitted-meta">{{ formatSubmittedAt(pendingAssignmentSubmission.submittedAt) }}</span>
              <span class="submitted-meta">録音長さ {{ formatTime(pendingAssignmentSubmission.durationSeconds) }}</span>
            </p>
          </div>
        </template>
      </div>
      <div class="spacer" />
      <button type="button" class="btn btn-back" @click="goBack">
        戻る
      </button>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'
import { useRecording } from '../composables/useRecording.js'
import { useSelectedLearner } from '../composables/useSelectedLearner.js'
import { todayJST } from '../composables/officialHolidays.js'
import { computeAssignmentSeekSeconds, normalizeAssignmentMarks } from '../utils/assignmentSeek.js'

const router = useRouter()
const route = useRoute()
const videoRef = ref(null)
const RESYNC_INTERVAL_MS = 5 * 60 * 1000
const lastResyncAt = ref(0)
const lastResyncYmd = ref('')
let isResyncing = false

// 認証ユーザー（録音開始時に必要）
const currentUser = ref(null)

const { selectedLearnerId, loadLearners } = useSelectedLearner()

// 単一解釈 API（learner-membership-status）の提出可否
const membershipStatusLoaded = ref(false)
const membershipCanSubmitToday = ref(false)
const membershipMessage = ref('')

// 動画Dayの上限（181以降も追加予定・5桁以上にはならない想定）
const MAX_PRACTICE_DAY = 9999

// Day: Top から渡された query.day を優先（提出先・動画URLに使用）。なければ進捗から取得
const parseDayFromQuery = () => {
  const n = Number(route.query.day)
  if (!Number.isFinite(n) || n < 1 || n > MAX_PRACTICE_DAY) return null
  return n
}
const currentDay = ref(parseDayFromQuery() ?? 1)

/** GET /api/learner-membership-status — 提出可否はサーバー一元化 */
async function fetchMembershipStatus() {
  const lid = selectedLearnerId.value
  if (!lid) {
    membershipStatusLoaded.value = true
    membershipCanSubmitToday.value = false
    membershipMessage.value = '学習者が選択されていません。'
    return { can_access: false, info: null }
  }
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) {
    membershipStatusLoaded.value = true
    membershipCanSubmitToday.value = false
    membershipMessage.value = '会員状態を取得できませんでした。'
    return { can_access: false, info: null, failed: true }
  }
  const base = getApiBase()
  const url = `${base ? base + '/' : ''}api/learner-membership-status?learner_id=${encodeURIComponent(lid)}`
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  })
  membershipStatusLoaded.value = true
  if (!res.ok) {
    // 取得できなかった（通信・サーバーの不調）。未契約と扱わず、提出だけ止めて案内する（指示書 B）
    membershipCanSubmitToday.value = false
    membershipMessage.value = '会員状態を取得できませんでした。'
    return { can_access: false, info: null, failed: true }
  }
  const info = await res.json()
  membershipCanSubmitToday.value = !!info.can_submit_today
  membershipMessage.value = (info.message && String(info.message).trim()) || ''
  return { can_access: !!info.can_access, info }
}

// 録音開始ガード（サーバー計算の can_submit_today のみ）
const guardHoliday = () => {
  if (!membershipStatusLoaded.value) {
    alert('会員状態を確認しています。しばらくしてからお試しください。')
    return false
  }
  if (membershipCanSubmitToday.value) return true
  alert(
    membershipMessage.value?.trim() ||
      '今日は録音・提出はできません。'
  )
  return false
}

// 送信済み（処理中）表示用（録音停止押下直後にセット、Home 戻り後も localStorage で復元）
// ※同一端末・同一ブラウザの localStorage。キーは「親ユーザー＋学習者」で分離（兄弟共有端末で表示が混ざらないようにする）
const PENDING_STORAGE_PREFIX = 'otoeigo_practice_submitted_'
const pendingPracticeSubmission = ref(null)
const pendingAssignmentSubmission = ref(null)
// 課題が複数ページのDay：ページ番号ごとの送信済み表示
const pendingAssignmentParts = ref({})
// 課題録音ボタンごとの移動先（複数課題のDayのみ。通常のDayは空＝動画の終わりの1秒前へ移動）
const assignmentMarks = ref([])
const hasMultipleAssignments = computed(() => assignmentMarks.value.length >= 2)
const lastAssignmentPartNo = computed(() => assignmentMarks.value[assignmentMarks.value.length - 1]?.partNo ?? null)
const currentAssignmentPartNo = ref(null)
const currentAssignmentLabel = computed(() =>
  currentAssignmentPartNo.value != null && hasMultipleAssignments.value
    ? assignmentMarks.value.find((m) => m.partNo === currentAssignmentPartNo.value)?.label || ''
    : ''
)

const formatSubmittedAt = (date) => {
  if (!date) return ''
  const d = typeof date === 'number' ? new Date(date) : date
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// 1キー = 親ユーザー × 学習者（learner 未選択時は従来どおり親のみ＝単一子の後方互換）
const getPendingStorageKey = () => {
  const userId = currentUser.value?.id
  if (!userId) return null
  const lid = selectedLearnerId.value
  return lid
    ? `${PENDING_STORAGE_PREFIX}${userId}_learner_${lid}`
    : `${PENDING_STORAGE_PREFIX}${userId}`
}

const savePendingToStorage = (type, durationSeconds, partNo = null) => {
  const key = getPendingStorageKey()
  const day = currentDay.value
  const ymd = todayJST()
  if (!key || !day) return
  const entry = {
    submittedAt: new Date().toISOString(),
    durationSeconds: Math.max(0, Math.round(durationSeconds))
  }
  try {
    const raw = localStorage.getItem(key)
    let data = raw ? JSON.parse(raw) : null
    if (!data || data.ymd !== ymd) {
      data = { ymd, day, practice: null, assignment: null, assignmentParts: {} }
    }
    data[type] = entry
    if (type === 'assignment' && partNo != null) {
      data.assignmentParts = { ...(data.assignmentParts || {}), [partNo]: entry }
    }
    data.day = day
    localStorage.setItem(key, JSON.stringify(data))
  } catch (e) {
    console.warn('localStorage setItem:', e)
  }
}

const loadTodaySubmittedState = () => {
  pendingPracticeSubmission.value = null
  pendingAssignmentSubmission.value = null
  pendingAssignmentParts.value = {}
  const key = getPendingStorageKey()
  const day = currentDay.value
  const todayYmd = todayJST()
  if (!key || !day) return
  try {
    let raw = localStorage.getItem(key)
    // 旧キー（親userIdのみ）→ 兄弟対応後キーへの一回だけ引き継ぎ（1人子でも learnerId が付くため）
    if (!raw && selectedLearnerId.value && currentUser.value?.id) {
      const legacyKey = `${PENDING_STORAGE_PREFIX}${currentUser.value.id}`
      raw = localStorage.getItem(legacyKey)
      if (raw) {
        try {
          localStorage.setItem(key, raw)
          localStorage.removeItem(legacyKey)
        } catch (_) {
          /* 引き継ぎ失敗時は legacy のまま読む */
        }
      }
    }
    if (!raw) return
    const data = JSON.parse(raw)
    if (data?.ymd !== todayYmd) return
    // 別Dayへ遷移したときに前の画面の「送信済み」が残らないようにする
    if (data.day != null && Number(data.day) !== Number(day)) return
    if (data.practice) {
      pendingPracticeSubmission.value = {
        submittedAt: data.practice.submittedAt ? new Date(data.practice.submittedAt) : new Date(),
        durationSeconds: Number(data.practice.durationSeconds) || 0
      }
    }
    if (data.assignmentParts && typeof data.assignmentParts === 'object') {
      const parts = {}
      for (const [no, e] of Object.entries(data.assignmentParts)) {
        parts[no] = {
          submittedAt: e?.submittedAt ? new Date(e.submittedAt) : new Date(),
          durationSeconds: Number(e?.durationSeconds) || 0
        }
      }
      pendingAssignmentParts.value = parts
    }
    if (data.assignment) {
      pendingAssignmentSubmission.value = {
        submittedAt: data.assignment.submittedAt ? new Date(data.assignment.submittedAt) : new Date(),
        durationSeconds: Number(data.assignment.durationSeconds) || 0
      }
    }
  } catch (_) {
    // ignore
  }
}

// iOS 判定（動画＋録音同時時の音量低下対策で使用）
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

// 録音機能（Recording と同じロジック）
const {
  isRecording,
  currentRecordingDay,
  currentRecordingType,
  canStopRecording,
  displayTime,
  showProductionUploadProgress,
  productionUploadProgress,
  uploadStatus,
  reliableRecorder,
  startPracticeRecording,
  startAssignmentRecording,
  stopRecording,
  formatTime
} = useRecording({
  currentUserRef: currentUser,
  selectedLearnerIdRef: selectedLearnerId,
  guardHoliday,
  onRecordingStopRequested: (type, durationSeconds) => {
    const payload = { submittedAt: new Date(), durationSeconds: Math.max(0, Math.round(durationSeconds)) }
    if (type === 'practice') {
      pendingPracticeSubmission.value = payload
      savePendingToStorage('practice', durationSeconds)
    } else if (type === 'assignment') {
      pendingAssignmentSubmission.value = payload
      savePendingToStorage('assignment', durationSeconds, currentAssignmentPartNo.value)
      if (currentAssignmentPartNo.value != null) {
        pendingAssignmentParts.value = { ...pendingAssignmentParts.value, [currentAssignmentPartNo.value]: payload }
      }
    }
  },
  onSubmissionSuccess: () => {
    console.log('✅ 提出完了')
  },
  // iOS: マイク取得で音量が下がることがあるため、録音開始後に volume=1 を再設定（play はユーザージェスチャ内で行うためここでは呼ばない）
  onRecordingStarted: () => {
    if (isIOS && currentRecordingType.value === 'practice') {
      const video = videoRef.value
      if (video && videoSrc.value && !videoLoadError.value) {
        video.volume = 1
      }
    }
  }
})

// 録音ボタン無効化（会員＋動画の両方）
const isRecordingStartDisabled = computed(() => {
  if (!membershipStatusLoaded.value) return true
  if (!membershipCanSubmitToday.value) return true
  if (videoLoadError.value) return true
  if (videoLoading.value) return true
  if (!videoSrc.value) return true
  return false
})

// 録音ボタンが disabled のときクリックできずアラートも出ないため、画面上で理由を示す
const recordingStartBlockedReason = computed(() => {
  if (!membershipStatusLoaded.value) {
    return '会員状態を確認しています。このままお待ちください。'
  }
  if (!membershipCanSubmitToday.value) {
    return (
      membershipMessage.value?.trim() ||
      '今日は録音・提出はできません。'
    )
  }
  if (videoLoadError.value === 'forbidden') {
    return 'このDayの動画は視聴できません。'
  }
  if (videoLoadError.value === 'error') {
    return '動画の読み込みに失敗しました。通信環境をご確認のうえ、再度お試しください。'
  }
  if (videoLoading.value) {
    return '動画を読み込み中です。完了してからお試しください。'
  }
  if (!videoSrc.value) {
    return '動画の準備ができていません。しばらくしてから再度お試しください。'
  }
  return ''
})

// 動画URL（署名付きURLをAPIから取得・不正アクセス対策）
const videoSrc = ref('')
const videoLoadError = ref(null) // 'forbidden' | 'error' | null
const videoLoading = ref(false)

const fetchPracticeVideoUrl = async (day) => {
  if (!day || day < 1 || day > MAX_PRACTICE_DAY) return
  videoLoadError.value = null
  videoLoading.value = true
  assignmentMarks.value = []
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      videoLoadError.value = 'error'
      return
    }
    const base = getApiBase()
    const lid = selectedLearnerId.value
    const url = `${base ? base + '/' : ''}api/practice-video-url?day=${day}${lid ? `&learnerId=${encodeURIComponent(lid)}` : ''}`
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    })
    if (res.status === 403) {
      videoLoadError.value = 'forbidden'
      videoSrc.value = ''
      return
    }
    if (!res.ok) {
      videoLoadError.value = 'error'
      videoSrc.value = ''
      return
    }
    const json = await res.json()
    const videoUrl = (json.url && String(json.url).trim()) || ''
    if (!videoUrl) {
      videoLoadError.value = 'error'
      videoSrc.value = ''
      return
    }
    videoSrc.value = videoUrl
    assignmentMarks.value = normalizeAssignmentMarks(json.assignmentMarks)
  } catch (err) {
    console.error('動画URL取得エラー:', err)
    videoLoadError.value = 'error'
    videoSrc.value = ''
  } finally {
    videoLoading.value = false
  }
}

// 動画要素の読み込みエラー（形式非対応・URL無効・ネットエラーなど）
const onVideoLoadError = () => {
  videoLoadError.value = 'error'
}

// 課題録音中かどうか（動画コントロール無効化用）
const isAssignmentRecording = computed(() =>
  isRecording.value && currentRecordingType.value === 'assignment'
)

// 練習録音: 動画再生と録音を同時開始（iOS対応）
// iOS では play() がユーザージェスチャ内でないとブロックされるため、必ずクリック直後に再生する
// 動画が未読み込み・再生不可でも録音は開始する
const startPracticeWithVideo = () => {
  const video = videoRef.value
  if (video && videoSrc.value && !videoLoadError.value) {
    video.currentTime = 0
    if (isIOS) video.volume = 1
    const p = video.play()
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        console.warn('動画再生失敗（録音は継続）:', err?.message || err)
        if (err?.name === 'NotSupportedError' || (err?.message && err.message.includes('supported sources'))) {
          videoLoadError.value = 'error'
        }
      })
    }
  }
  startPracticeRecording(currentDay.value)
}

// 課題録音: 開始時に動画を一時停止し、課題が書かれた場所へ自動で移動してから録音する。
//   通常のDay：動画の終わりの1秒前（課題は最後の1秒に書かれている）
//   複数課題のDay：押したボタン（mark）の移動先の秒数
// 移動と録音の開始は同じクリックの中で同期的に行う（iOSは、ユーザー操作から切り離すと録音の開始に失敗することがあるため、
// 移動の完了を待たない）。動画の長さが取れない場合は、移動せず録音だけ始める（録音は止めない）。
const seekVideoForAssignment = (mark) => {
  const video = videoRef.value
  if (!video || !videoSrc.value || videoLoadError.value) return
  try {
    const target = computeAssignmentSeekSeconds({ duration: video.duration, seekSeconds: mark?.seekSeconds ?? null })
    if (target != null) video.currentTime = target
  } catch (err) {
    console.warn('課題位置への移動に失敗（録音は継続）:', err?.message || err)
  }
}

const startAssignmentRecordingOnly = (mark = null) => {
  videoRef.value?.pause()
  seekVideoForAssignment(mark)
  currentAssignmentPartNo.value = mark?.partNo ?? null
  startAssignmentRecording(currentDay.value)
}

const loadProgress = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    router.push('/')
    return
  }
  await loadLearners()
  try {
    const { can_access: canAccess, failed: membershipFailed } = await fetchMembershipStatus()
    // 取得できなかったときは未契約と扱わない（提出だけ止めて案内済み）。利用できないときは、Top で
    // 利用できる兄弟への切り替え、または（誰も利用できなければ）マイページを案内する（指示書 B）
    if (!membershipFailed && !canAccess) {
      router.push('/top')
      return
    }
  } catch (e) {
    console.warn('会員状態チェック中にエラーが発生しました:', e)
    membershipStatusLoaded.value = true
    membershipCanSubmitToday.value = false
    membershipMessage.value = '会員状態を取得できませんでした。'
  }
  currentUser.value = { id: user.id }
  // Top から day が渡されていればそのまま使用（提出先・動画と同期）
  if (parseDayFromQuery() != null) return
  const lid = selectedLearnerId.value
  try {
    if (lid) {
      const { data: dayAfterAdvance, error: rpcError } = await supabase.rpc('advance_current_day_if_eligible_for_learner', { p_learner_id: lid })
      if (!rpcError && typeof dayAfterAdvance === 'number') {
        currentDay.value = dayAfterAdvance
        return
      }
      const { data: learner } = await supabase.from('learners').select('current_day').eq('id', lid).single()
      currentDay.value = learner?.current_day ?? 1
    } else {
      const { data: dayAfterAdvance, error: rpcError } = await supabase.rpc('advance_current_day_if_eligible')
      if (!rpcError && typeof dayAfterAdvance === 'number') {
        currentDay.value = dayAfterAdvance
        return
      }
      const { data: profile } = await supabase.from('users_phase2').select('current_day').eq('id', user.id).single()
      currentDay.value = profile?.current_day ?? 1
    }
  } catch (err) {
    console.error('進捗取得エラー:', err)
    currentDay.value = 1
  }
}

// 兄弟切り替え時に進捗・提出状態を再読み込み
watch(selectedLearnerId, async () => {
  if (currentUser.value?.id) {
    await loadProgress()
    loadTodaySubmittedState()
    await fetchPracticeVideoUrl(currentDay.value)
  }
})

const goBack = () => {
  router.push('/top')
}

const todayYmdJst = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })

const shouldResyncOnResume = () => {
  const now = Date.now()
  const ymd = todayYmdJst()
  if (!lastResyncAt.value) return true
  if (ymd !== lastResyncYmd.value) return true
  return (now - lastResyncAt.value) >= RESYNC_INTERVAL_MS
}

const markResynced = () => {
  lastResyncAt.value = Date.now()
  lastResyncYmd.value = todayYmdJst()
}

const resyncOnResume = async () => {
  if (isResyncing || isRecording.value || !shouldResyncOnResume()) return
  isResyncing = true
  try {
    await loadProgress()
    loadTodaySubmittedState()
    await fetchPracticeVideoUrl(currentDay.value)
    markResynced()
  } catch (e) {
    console.warn('Practice resume resync error:', e)
  } finally {
    isResyncing = false
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    resyncOnResume()
  }
}

const handleWindowFocus = () => {
  resyncOnResume()
}

onMounted(async () => {
  await loadProgress()
  loadTodaySubmittedState()
  await fetchPracticeVideoUrl(currentDay.value)
  markResynced()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleWindowFocus)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('focus', handleWindowFocus)
  if (reliableRecorder.value) {
    reliableRecorder.value.stopRecording()
  }
})
</script>

<style scoped>
.practice-page {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
  min-height: 0; /* flex子が縮小できるように */
  overflow: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 動画エリア（横向き: 左85%） */
.video-area {
  width: 80%;
  min-width: 0; /* flexで縮小し、右側ボタンエリアが隠れないようにする */
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-player {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

/* 動画コントロールのボタン非表示（ダウンロード・フルスクリーン等） */
.video-player::-webkit-media-controls-fullscreen-button {
  display: none !important;
}
.video-player::-webkit-media-controls-download-button {
  display: none !important;
}
.video-player::-webkit-media-controls-playback-rate-button {
  display: none !important;
}
.video-player::-webkit-media-controls-picture-in-picture-button {
  display: none !important;
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 120px;
  color: #64748b;
  font-size: 1rem;
}
.video-placeholder.video-error {
  color: #dc2626;
}

/* ボタンエリア（横向き: 右15%縦並び） */
.button-area {
  width: 20%;
  min-width: 120px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 1rem 0.75rem;
  gap: 0.75rem;
  background: #f1f5f9;
  border-left: 1px solid #e2e8f0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.recording-block-notice {
  margin: 0 0 0.5rem;
  padding: 0.6rem 0.5rem;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  text-align: left;
}

.btn {
  width: 100%;
  padding: 0.75rem 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}

.btn-action {
  background: #e2e8f0;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.btn-action:hover {
  background: #cbd5e1;
}

.spacer {
  flex: 0 0 auto;
  min-height: 0.5rem;
}

.btn-back {
  flex-shrink: 0;
  background: #64748b;
  color: white;
  margin-top: auto;
}

.btn-back:hover {
  background: #475569;
}

/* 録音中インライン表示（Recording と同じ挙動） */
.recording-status-inline {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}
.recording-status-inline .recording-label {
  font-weight: 600;
  color: #334155;
}
.recording-status-inline .recording-time {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}
.recording-status-inline .assignment-notice {
  margin: 0;
  padding: 0.5rem;
  font-size: 0.85rem;
  color: #0369a1;
  background: #e0f2fe;
  border-radius: 6px;
  text-align: center;
}
.btn-stop-recording {
  background: #dc2626;
  color: white;
}
.btn-stop-recording:hover:not(:disabled) {
  background: #b91c1c;
}
.btn-stop-recording:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.metacognitive-prompt {
  margin: 0;
  padding: 0.45rem 0.5rem;
  font-size: 0.78rem;
  line-height: 1.5;
  color: #0f766e;
  background: #ccfbf1;
  border: 1px solid #99f6e4;
  border-radius: 6px;
  text-align: left;
}

/* 送信済み表示（録音停止直後の達成感用） */
.action-with-status {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
}
.submitted-status {
  margin: 0;
  padding: 0.4rem 0.5rem;
  font-size: 0.8rem;
  color: #065f46;
  background: #d1fae5;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.submitted-badge {
  font-weight: 600;
}
.submitted-meta {
  font-size: 0.75rem;
  color: #047857;
}

/* 本番用アップロード進捗オーバーレイ（Recording と同じ） */
.production-upload-progress-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}
.production-upload-progress-modal {
  background: white;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  min-width: 320px;
  text-align: center;
}
.production-upload-progress-modal .upload-message h3 {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: bold;
}
.production-upload-progress-modal .upload-message p {
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 1rem;
}
.production-upload-progress-modal .progress-container {
  margin-bottom: 1.5rem;
}
.production-upload-progress-modal .progress-bar {
  width: 100%;
  height: 24px;
  background: #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}
.production-upload-progress-modal .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transition: width 0.3s ease;
  border-radius: 12px;
}
.production-upload-progress-modal .progress-text {
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 500;
}
.production-upload-progress-modal .completion-message {
  color: #059669;
  font-size: 1.2rem;
  font-weight: bold;
}

/* ========== 縦向き（スマホ・タブレット縦持ち） ========== */
@media (orientation: portrait) {
  .practice-page {
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
  }

  /* 動画: 上寄せ・左右マージンゼロで画面幅100%（端まで）・比率維持 */
  .video-area {
    width: 100%;
    height: auto;
    flex: 0 0 auto;
    align-items: flex-start;
    justify-content: center;
    margin: 0;
    padding: 0;
  }

  .video-wrapper {
    width: 100%;
    height: auto;
    min-height: 0;
    display: block;
    margin: 0;
    padding: 0;
  }

  .video-player {
    width: 100%;
    max-width: 100%;
    height: auto;
    max-height: none;
    display: block;
    vertical-align: top;
    margin: 0;
  }

  /* ボタン: 動画の下にスペースをあけ、①②横一列・戻るは左下で手が触れにくい位置 */
  .button-area {
    width: 100%;
    min-width: 0;
    height: auto;
    flex: 1 1 auto;
    flex-direction: column;
    padding: 1rem;
    gap: 0.75rem;
    border-left: none;
    border-top: 1px solid #e2e8f0;
  }

  .action-buttons {
    flex: 0 0 auto;
    min-height: 0;
    overflow: visible;
    flex-direction: row;
    justify-content: center;
    gap: 0.5rem;
  }

  .action-buttons .btn {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    padding: 0.65rem 0.4rem;
  }

  .metacognitive-prompt {
    font-size: 0.8rem;
  }

  .spacer {
    flex: 1;
    min-height: 0.5rem;
  }

  .btn-back {
    margin-top: 0;
    align-self: flex-start;
    width: auto;
    min-width: 100px;
    margin-left: 0.5rem;
    margin-bottom: 1.5rem;
  }
}
</style>
